import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import reviewsData from "../data/reviews.json";

// ─── Configuration ────────────────────────────────────────────────────────────
const ITEMS_PER_SLIDE = 3;   // Cards shown per slide on desktop
const SLIDE_SPEED_MS  = 4000; // Auto-advance interval in milliseconds
const SHOW_ARROWS     = true; // Show prev / next arrow buttons
const SHOW_DOTS       = true; // Show dot indicators below carousel
// ─────────────────────────────────────────────────────────────────────────────

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.45, ease: "easeInOut" },
  },
  exit: (direction) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0,
    transition: { duration: 0.45, ease: "easeInOut" },
  }),
};

const MAX_CHARS = 195;

const ReviewCard = ({ name, profession, review }) => {
  const [expanded, setExpanded] = useState(false);
  const isTruncated = review.length > MAX_CHARS;

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-3 h-full" style={{ minHeight: "200px" }}>
      <div className="flex-1">
        <p className="text-gray-600 text-sm leading-relaxed text-justify">
          {isTruncated && !expanded
            ? <>{review.slice(0, MAX_CHARS).trimEnd()}…<button onClick={() => setExpanded(true)} className="ml-1 text-xs font-medium text-[#30D5C8] hover:underline focus:outline-none">Read more</button></>
            : <>{review}{isTruncated && <button onClick={() => setExpanded(false)} className="ml-1 text-xs font-medium text-[#30D5C8] hover:underline focus:outline-none">Show less</button>}</>
          }
        </p>
      </div>
      <div className="mt-2">
        <p className="font-bold text-gray-800 text-sm">{name}</p>
        <p className="text-gray-400 text-xs mt-0.5">{profession}</p>
      </div>
    </div>
  );
};

const ReviewsSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const perSlide   = isMobile ? 1 : ITEMS_PER_SLIDE;
  const totalSlides = Math.ceil(reviewsData.length / perSlide);

  const [[page, direction], setPage] = useState([0, 0]);
  const [paused, setPaused] = useState(false);

  const paginate = useCallback(
    (newDirection) => {
      setPage(([prev]) => [
        (prev + newDirection + totalSlides) % totalSlides,
        newDirection,
      ]);
    },
    [totalSlides]
  );

  // Reset to page 0 when perSlide changes (mobile ↔ desktop)
  useEffect(() => {
    setPage([0, 0]);
  }, [perSlide]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => paginate(1), SLIDE_SPEED_MS);
    return () => clearInterval(id);
  }, [paused, paginate]);

  const slideReviews = reviewsData.slice(
    page * perSlide,
    page * perSlide + perSlide
  );

  return (
    <section id="reviews" className="py-8 lg:py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Heading */}
        <h2 className="text-4xl font-bold text-[#30D5C8] text-center mb-6">
          Reviews
        </h2>

        {/* Carousel wrapper */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Slide */}
          <div className="overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={page}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={`grid gap-6 ${
                  perSlide === 1
                    ? "grid-cols-1"
                    : perSlide === 2
                    ? "grid-cols-2"
                    : "grid-cols-3"
                }`}
              >
                {slideReviews.map((r) => (
                  <ReviewCard
                    key={r.id}
                    name={r.name}
                    profession={r.profession}
                    review={r.review}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Arrow buttons */}
          {SHOW_ARROWS && (
            <>
              <button
                onClick={() => paginate(-1)}
                aria-label="Previous slide"
                className="absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200 shadow-md rounded-full p-2 text-gray-600 hover:text-[#30D5C8] hover:border-[#30D5C8] transition-colors z-10"
              >
                <FaChevronLeft size={16} />
              </button>
              <button
                onClick={() => paginate(1)}
                aria-label="Next slide"
                className="absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200 shadow-md rounded-full p-2 text-gray-600 hover:text-[#30D5C8] hover:border-[#30D5C8] transition-colors z-10"
              >
                <FaChevronRight size={16} />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {SHOW_DOTS && totalSlides > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage([i, i > page ? 1 : -1])}
                aria-label={`Go to slide ${i + 1}`}
                className={`transition-all duration-300 rounded-full ${
                  i === page
                    ? "w-6 h-3 bg-[#30D5C8]"
                    : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
